import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, ShoppingBag, Menu, X, MessageCircle } from "lucide-react";
import { NAV } from "../data/content";
import { WHATSAPP_URL } from "../lib/site";
import { useCart } from "../context/CartContext";
import { useWishlist } from "../context/WishlistContext";
import { useScrolledPast } from "../hooks/useScrolledPast";

const Logo = ({ className = "" }) => (
  <Link
    to="/"
    data-testid="logo-link"
    className={`font-serif leading-none tracking-tight ${className}`}
  >
    <span className="text-2xl md:text-[1.7rem] font-medium text-charcoal">Bath</span>
    <span className="text-2xl md:text-[1.7rem] font-light italic text-taupe">Lane</span>
  </Link>
);

export const Header = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { count } = useCart();
  const { count: wishCount } = useWishlist();
  const onHome = location.pathname === "/";
  const scrolled = useScrolledPast(24);

  // On the vanities page the header always has a solid background.
  const solid = scrolled || !onHome;

  const goAnchor = (hash) => {
    if (onHome) {
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/" + hash);
    }
  };

  const iconBtn =
    "grid place-items-center h-10 w-10 rounded-full text-charcoal transition-colors duration-300 hover:bg-stone/70";

  const testId = (label) => `nav-${label.toLowerCase().replace(/\s+/g, "-")}`;

  const renderNavItem = (item, mobile = false) => {
    const base = mobile
      ? "font-serif text-4xl font-light text-charcoal py-3 border-b border-beige/60 text-left"
      : "link-sweep text-[0.8rem] font-medium tracking-wide text-charcoal/80 hover:text-charcoal transition-colors";
    const tid = `${mobile ? "mobile-" : ""}${testId(item.label)}`;

    if (item.type === "route") {
      return (
        <Link
          key={item.label}
          to={item.to}
          data-testid={tid}
          onClick={() => mobile && setOpen(false)}
          className={base}
        >
          {item.label}
        </Link>
      );
    }
    return (
      <button
        key={item.label}
        data-testid={tid}
        onClick={() => {
          if (mobile) setOpen(false);
          goAnchor(item.hash);
        }}
        className={base}
      >
        {item.label}
      </button>
    );
  };

  return (
    <>
      <motion.header
        data-testid="site-header"
        initial={{ y: -80 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
        className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,padding] duration-500 ${
          solid
            ? "bg-ivory/95 backdrop-blur-sm border-b border-beige py-3"
            : "bg-transparent border-b border-transparent py-5"
        }`}
      >
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 flex items-center justify-between gap-6">
          <Logo />

          <nav className="hidden lg:flex items-center gap-8" data-testid="primary-nav">
            {NAV.map((item) => renderNavItem(item))}
          </nav>

          <div className="flex items-center gap-1 md:gap-2">
            <Link to="/wishlist" className={`${iconBtn} relative`} data-testid="wishlist-button" aria-label="Wishlist">
              <Heart strokeWidth={1.5} size={19} />
              {wishCount > 0 && (
                <span
                  data-testid="wishlist-count"
                  className="absolute -top-0.5 -right-0.5 grid place-items-center h-[18px] min-w-[18px] px-1 rounded-full bg-charcoal text-ivory text-[0.62rem] font-medium"
                >
                  {wishCount}
                </span>
              )}
            </Link>
            <Link to="/cart" className={`${iconBtn} relative`} data-testid="cart-button" aria-label="Cart">
              <ShoppingBag strokeWidth={1.5} size={19} />
              {count > 0 && (
                <span
                  data-testid="cart-count"
                  className="absolute -top-0.5 -right-0.5 grid place-items-center h-[18px] min-w-[18px] px-1 rounded-full bg-charcoal text-ivory text-[0.62rem] font-medium"
                >
                  {count}
                </span>
              )}
            </Link>
            <a
              href={WHATSAPP_URL()}
              target="_blank"
              rel="noreferrer"
              data-testid="header-whatsapp"
              className="hidden md:inline-flex items-center gap-2 ml-2 rounded-full border border-charcoal px-4 py-2 text-[0.75rem] font-medium text-charcoal btn-fill hover:text-charcoal transition-colors duration-500"
            >
              <MessageCircle strokeWidth={1.5} size={15} />
              WhatsApp
            </a>
            <button
              className={`${iconBtn} lg:hidden`}
              data-testid="mobile-menu-toggle"
              aria-label="Menu"
              onClick={() => setOpen(true)}
            >
              <Menu strokeWidth={1.5} size={22} />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[70] bg-ivory lg:hidden"
            data-testid="mobile-menu"
            initial={{ clipPath: "inset(0 0 100% 0)" }}
            animate={{ clipPath: "inset(0 0 0% 0)" }}
            exit={{ clipPath: "inset(0 0 100% 0)" }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-beige">
              <Logo />
              <button
                className={iconBtn}
                onClick={() => setOpen(false)}
                data-testid="mobile-menu-close"
                aria-label="Close menu"
              >
                <X strokeWidth={1.5} size={24} />
              </button>
            </div>
            <nav className="flex flex-col px-6 py-8">
              {NAV.map((item, i) => (
                <motion.div
                  key={item.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 + i * 0.06, duration: 0.5 }}
                  className="flex flex-col"
                >
                  {renderNavItem(item, true)}
                </motion.div>
              ))}
              <Link
                to="/wishlist"
                onClick={() => setOpen(false)}
                data-testid="mobile-nav-wishlist"
                className="mt-8 inline-flex items-center justify-center gap-2 rounded-full border border-charcoal px-6 py-4 text-sm font-medium text-charcoal"
              >
                <Heart strokeWidth={1.6} size={17} /> Wishlist{wishCount > 0 ? ` (${wishCount})` : ""}
              </Link>
              <a
                href={WHATSAPP_URL()}
                target="_blank"
                rel="noreferrer"
                className="mt-3 inline-flex items-center justify-center gap-2 rounded-full bg-charcoal px-6 py-4 text-sm font-medium text-ivory btn-fill hover:text-charcoal transition-colors duration-500"
                data-testid="mobile-whatsapp"
              >
                <MessageCircle strokeWidth={1.5} size={17} /> Chat on WhatsApp
              </a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
